import React, { useEffect, useRef } from "react";
import { UseFormRegisterReturn } from "react-hook-form";

import { Textarea } from "./textarea";

import { Input } from "@/components/ui/input";
import useAutosizeTextArea from "@/hooks/useAutosizeTextArea";
import { cn } from "@/lib/utils";

interface PrefixPostfixProps {
  node: React.ReactNode;
  className?: string;
}

interface CommonProps {
  register?: UseFormRegisterReturn;
}

type BaseInputProps = CommonProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix">;

type BaseInputAreaProps = CommonProps &
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "prefix">;

interface BaseInputWithAffixesProps {
  prefix?: PrefixPostfixProps;
  postfix?: PrefixPostfixProps;
}

interface InputProps extends BaseInputProps, BaseInputWithAffixesProps {
  inputType: "input";
}

interface TextareaProps extends BaseInputAreaProps, BaseInputWithAffixesProps {
  inputType: "textarea";
}

type InputWithAffixesProps = InputProps | TextareaProps;

export const InputWithAffixes: React.FC<InputWithAffixesProps> = ({
  prefix,
  postfix,
  inputType = "input",
  className,
  ...props
}) => {
  const prefixRef = useRef<HTMLDivElement>(null);
  const postfixRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePadding = (): void => {
      const input = prefixRef.current?.parentElement?.querySelector("input");
      const textarea =
        prefixRef.current?.parentElement?.querySelector("textarea");
      if (input && inputType === "input") {
        const prefixWidth = prefixRef.current?.offsetWidth || 0;
        const postfixWidth = postfixRef.current?.offsetWidth || 0;
        input.style.paddingLeft = prefix ? `${prefixWidth + 8}px` : "";
        input.style.paddingRight = postfix ? `${postfixWidth + 8}px` : "";
      } else if (textarea && inputType === "textarea") {
        const prefixWidth = prefixRef.current?.offsetWidth || 0;
        const postfixWidth = postfixRef.current?.offsetWidth || 0;
        textarea.style.paddingLeft = prefix ? `${prefixWidth + 8}px` : "";
        textarea.style.paddingRight = postfix ? `${postfixWidth + 8}px` : "";
      }
    };
    updatePadding();
  }, [prefix, postfix]);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textAreaRef.current, String(props.value || ""));

  return (
    <div className='relative w-full'>
      {inputType === "textarea" ? (
        <Textarea
          className={cn("w-full relative  ", className)}
          style={{
            overflow: "hidden",
          }}
          ref={textAreaRef}
          {...(props as React.InputHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <Input
          className={cn("w-full relative", className)}
          {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
        />
      )}

      {/* Prefix */}
      {prefix && (
        <div
          ref={prefixRef}
          className={cn(
            "absolute inset-y-0 left-0 flex h-full items-center justify-center",
            prefix.className,
          )}
        >
          {prefix.node}
        </div>
      )}

      {/* Postfix */}
      {postfix && (
        <div
          ref={postfixRef}
          className={cn(
            "absolute inset-y-0 right-0 flex h-full items-center justify-center",
            postfix.className,
          )}
        >
          {postfix.node}
        </div>
      )}
    </div>
  );
};
